"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useLocale } from "next-intl";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}
type Annotation = {
  id: string;
  num: string;
  label: string;
  value: string;
  // position around the paper: x/y in % of pinWrap
  x: number;
  y: number;
};
type Props = {
  kicker: string;
  title: string;
  annotations: Annotation[];
};
export function CvScanSection(t0) {
  const $ = _c(62);
  const {
    kicker,
    title,
    annotations
  } = t0;
  const locale = useLocale();
  const isEn = locale === "en";
  let t1;
  if ($[0] !== isEn) {
    t1 = isEn ? {
      subtitle: "developer \xB7 paris",
      experience: "EXPERIENCE",
      skills: "SKILLS",
      education: "EDUCATION",
      chip: "SENIOR",
      scan: "scan"
    } : {
      subtitle: "d\xE9veloppeur\xB7se \xB7 paris",
      experience: "EXP\xC9RIENCE",
      skills: "COMP\xC9TENCES",
      education: "FORMATION",
      chip: "S\xC9NIOR",
      scan: "scan"
    };
    $[0] = isEn;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const labels = t1;
  const rootRef = useRef(null);
  const pinSpacerRef = useRef(null);
  const pinRef = useRef(null);
  const scannerRef = useRef(null);
  const progressRef = useRef(null);
  const progressTextRef = useRef(null);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const root = rootRef.current;
      const pin = pinRef.current;
      const scanner = scannerRef.current;
      const progress = progressRef.current;
      const pText = progressTextRef.current;
      if (!root || !pin || !scanner || !progress || !pText) {
        return;
      }
      const ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: pin,
            start: "top top",
            end: "+=1400",
            scrub: 0.5,
            pin: true,
            pinSpacer: pinSpacerRef.current ?? undefined
          }
        });
        tl.fromTo(scanner, {
          top: "0%",
          opacity: 0
        }, {
          opacity: 1,
          duration: 0.05
        }, 0).to(scanner, {
          top: "100%",
          duration: 1,
          ease: "none"
        }, 0.05).to(scanner, {
          opacity: 0,
          duration: 0.05
        }, 1);
        const counter = {
          v: 0
        };
        tl.to(progress, {
          width: "100%",
          duration: 1,
          ease: "none"
        }, 0).to(counter, {
          v: 100,
          duration: 1,
          ease: "none",
          onUpdate: () => {
            const textNode = pText.firstChild;
            if (textNode) {
              textNode.nodeValue = String(Math.round(counter.v));
            }
          }
        }, 0);
        const ann = gsap.utils.toArray(".l2-annotation");
        ann.forEach((el, i) => {
          const start = 0.2 + i / ann.length * 0.7;
          tl.to(el, {
            className: "l2-annotation show",
            duration: 0.01
          }, start);
        });
      }, root);
      return () => ctx.revert();
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      maxWidth: 1200,
      margin: "0 auto",
      marginBottom: 60
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      marginBottom: 24
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== kicker) {
    t6 = <p className="l2-mono" style={t5}>[ {kicker} ]</p>;
    $[6] = kicker;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      maxWidth: 900
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== title) {
    t8 = <h2 className="l2-display l2-display-lg" style={t7}>{title}</h2>;
    $[9] = title;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6 || $[12] !== t8) {
    t9 = <div style={t4}>{t6}{t8}</div>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      height: "100vh",
      minHeight: 700,
      display: "grid",
      placeItems: "center",
      position: "relative"
    };
    t11 = {
      position: "relative",
      display: "grid",
      placeItems: "center"
    };
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  let t12;
  let t13;
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      position: "relative"
    };
    t13 = <div className="l2-paper-line title" />;
    t14 = {
      fontSize: 11,
      color: "oklch(0.12 0.02 280 / 0.55)"
    };
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
  } else {
    t12 = $[16];
    t13 = $[17];
    t14 = $[18];
  }
  let t15;
  if ($[19] !== labels.subtitle) {
    t15 = <div style={t14}>{labels.subtitle}</div>;
    $[19] = labels.subtitle;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== labels.experience) {
    t16 = <div className="l2-paper-section">{labels.experience}</div>;
    $[21] = labels.experience;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="l2-paper-line" />;
    t18 = <div className="l2-paper-line mid" />;
    t19 = <div className="l2-paper-line short" />;
    t20 = <div className="l2-paper-line" />;
    $[23] = t17;
    $[24] = t18;
    $[25] = t19;
    $[26] = t20;
  } else {
    t17 = $[23];
    t18 = $[24];
    t19 = $[25];
    t20 = $[26];
  }
  let t21;
  if ($[27] !== labels.skills) {
    t21 = <div className="l2-paper-section">{labels.skills}</div>;
    $[27] = labels.skills;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  let t23;
  let t24;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="l2-paper-line" />;
    t23 = <div className="l2-paper-line mid" />;
    t24 = <div className="l2-paper-line short" />;
    $[29] = t22;
    $[30] = t23;
    $[31] = t24;
  } else {
    t22 = $[29];
    t23 = $[30];
    t24 = $[31];
  }
  let t25;
  if ($[32] !== labels.education) {
    t25 = <div className="l2-paper-section">{labels.education}</div>;
    $[32] = labels.education;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  let t26;
  let t27;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="l2-paper-line mid" />;
    t27 = <div className="l2-paper-line short" />;
    $[34] = t26;
    $[35] = t27;
  } else {
    t26 = $[34];
    t27 = $[35];
  }
  let t28;
  if ($[36] !== labels.chip) {
    t28 = <span className="l2-paper-chip">{labels.chip}</span>;
    $[36] = labels.chip;
    $[37] = t28;
  } else {
    t28 = $[37];
  }
  let t29;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div ref={scannerRef} className="l2-scanner" />;
    $[38] = t29;
  } else {
    t29 = $[38];
  }
  let t30;
  if ($[39] !== t15 || $[40] !== t16 || $[41] !== t21 || $[42] !== t25 || $[43] !== t28) {
    t30 = <div className="l2-paper" style={t12}>{t13}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}{t28}{t29}</div>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t21;
    $[42] = t25;
    $[43] = t28;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  let t31;
  if ($[45] !== annotations) {
    t31 = annotations.map(_temp);
    $[45] = annotations;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      position: "absolute",
      bottom: -80,
      left: 0,
      right: 0,
      display: "flex",
      alignItems: "center",
      gap: 16,
      fontFamily: "var(--font-mono)",
      fontSize: 11,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "var(--l2-fg-dim)"
    };
    $[47] = t32;
  } else {
    t32 = $[47];
  }
  let t33;
  if ($[48] !== labels.scan) {
    t33 = <span>{labels.scan}</span>;
    $[48] = labels.scan;
    $[49] = t33;
  } else {
    t33 = $[49];
  }
  let t34;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = {
      flex: 1,
      height: 2,
      background: "var(--l2-fg-faint)",
      position: "relative"
    };
    $[50] = t34;
  } else {
    t34 = $[50];
  }
  let t35;
  let t36;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div style={t34}><div ref={progressRef} style={{
        position: "absolute",
        left: 0,
        top: 0,
        height: "100%",
        width: "0%",
        background: "var(--l2-accent)",
        boxShadow: "0 0 8px var(--l2-accent)"
      }} /></div>;
    t36 = <span style={{
      color: "var(--l2-fg)",
      minWidth: 40,
      textAlign: "right"
    }}><span ref={progressTextRef}>0</span>%</span>;
    $[51] = t35;
    $[52] = t36;
  } else {
    t35 = $[51];
    t36 = $[52];
  }
  let t37;
  if ($[53] !== t33) {
    t37 = <div style={t32}>{t33}{t35}{t36}</div>;
    $[53] = t33;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  let t38;
  if ($[55] !== t30 || $[56] !== t31 || $[57] !== t37) {
    t38 = <div ref={pinSpacerRef}><div ref={pinRef} style={t10}><div style={t11}>{t30}{t31}{t37}</div></div></div>;
    $[55] = t30;
    $[56] = t31;
    $[57] = t37;
    $[58] = t38;
  } else {
    t38 = $[58];
  }
  let t39;
  if ($[59] !== t38 || $[60] !== t9) {
    t39 = <section className="l2-section" ref={rootRef}>{t9}{t38}</section>;
    $[59] = t38;
    $[60] = t9;
    $[61] = t39;
  } else {
    t39 = $[61];
  }
  return t39;
}
function _temp(a) {
  return <div key={a.id} className="l2-annotation" style={{
    left: `${a.x}%`,
    top: `${a.y}%`
  }}><div className="label"><span className="num">{a.num}</span> {a.label}</div><div className="value">{a.value}</div></div>;
}